// frontend/src/components/Register.js
//#region Imports
import { useState, useEffect, useContext, Fragment } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { LanguageContext } from '../../contexts/LanguageProvider';
import { AuthContext } from '../../contexts/AuthContext';
import estado from '../../utils/estados.json';
import { API_ENDPOINTS, EXTERNAL_API } from '../../configs/apiConfig';
import '../../styles/Register.css';
import '../../styles/terms-modal.css';
import translations from './translations';
import { useAlertContext } from '../../contexts/AlertContext';
import BrandSelect from '../common/BrandSelect';
import {
  FaArrowLeft, FaCheck, FaEye, FaEyeSlash, FaLock,
  FaTractor, FaUserGraduate, FaFlask, FaUserTie, FaEllipsisH
} from 'react-icons/fa';
//#endregion

//#region Componente Principal
function Register() {
  //#region Declarações
  const { isLoggedIn, setLoginModalOpen } = useContext(AuthContext);
  const navigate = useNavigate();
  if (isLoggedIn) {
    const role = localStorage.getItem('role');
    navigate(`/${role.toLowerCase()}`);
  }

  const { language } = useContext(LanguageContext);
  const [cropData, setCropData] = useState(["loading..."]);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [dataCollectionConsent, setDataCollectionConsent] = useState(false);
  const [step, setStep] = useState(1);
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [user, setUser] = useState({
    username: '',
    password: '',
    name: '',
    role: null,
    email: '',
    subtype: '',
    company: '',
    plantingChallengesDescription: '',
    isInvolvedWithBiological: null,
    isFarmerChild: null,
    conductsCoffeeResearch: null,
    cropType: '',
    state: '',
    city: '',
    areaplanted: '',
    educationalInstitution: '',
    permissionToCollectGeographicData: true,
    platformInterest: '',
    cpf: '',
    dateBirth: '',
    otherDescription: '',
  });
  const [passwordStrength, setPasswordStrength] = useState({ level: 0, label: '', color: '' });
  const [cityList, setCityList] = useState([]);
  const [cpfError, setCpfError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [showOccupationModal, setShowOccupationModal] = useState(false);
  const [pendingOccupation, setPendingOccupation] = useState('');

  const { showSuccess, showError } = useAlertContext();

  const t = translations[language] || translations.pt;
  //#endregion

  //#region Efeitos (useEffect)
  useEffect(() => {
    async function fetchData() {
      try {
        const response = await axios.get(API_ENDPOINTS.PLANTING_TYPES);
        setCropData(response.data);
      } catch (error) {
        showError(t.errorFetching, 50000);
      }
    }
    fetchData();
  }, [showError, t.errorFetching]);

  useEffect(() => {
    async function fetchEstado() {
      try {
        if (user.state) {
          const response = await axios.get(EXTERNAL_API.IBGE_CITIES_BY_STATE(user.state));
          setCityList(response.data);
          setUser(u => ({ ...u, city: "" }));
        }
      } catch (error) {
        showError(t.errorFetching, 50000);
      }
    }
    fetchEstado();
  }, [user.state, showError, t.errorFetching]);
  //#endregion

  //#region Funções de Validação
  const getPasswordStrength = (password) => {
    if (!password) return { level: 0, label: '', color: '' };
    let strength = 0;
    const checks = {
      minLength: password.length >= 6,
      goodLength: password.length >= 12,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    if (checks.minLength) strength++;
    if (checks.goodLength) strength++;
    if (checks.hasUpperCase) strength++;
    if (checks.hasLowerCase) strength++;
    if (checks.hasNumber) strength++;
    if (checks.hasSpecialChar) strength++;

    let level, label, color;
    if (strength <= 2) {
      level = 1; label = t.passwordWeak || 'Fraca'; color = '#d4a017';
    } else if (strength === 3) {
      level = 2; label = t.passwordMedium || 'Média'; color = '#339288';
    } else {
      level = 3; label = t.passwordStrong || 'Forte'; color = '#2aa44e';
    }
    return { level, label, color, checks };
  };

  const formatCPF = (value) => value
    .replace(/\D/g, '')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1');

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // Gera um username a partir do e-mail (login continua por username no backend)
  const usernameFromEmail = (email) =>
    (email || '').split('@')[0].toLowerCase().replace(/[^a-z0-9._-]/g, '').slice(0, 30);

  const validateOccupationFields = () => {
    const requiredFields = {
      PRODUCER: ['cropType', 'areaplanted', 'plantingChallengesDescription'],
      STUDENT: ['educationalInstitution'],
      CONSULTANT: ['company', 'isInvolvedWithBiological'],
      RESEARCHER: ['educationalInstitution', 'conductsCoffeeResearch', 'isInvolvedWithBiological'],
      OTHER: ['otherDescription']
    };
    const fieldsToValidate = requiredFields[user.subtype] || [];
    const missingFields = [];
    fieldsToValidate.forEach(field => {
      if (field === 'isInvolvedWithBiological' || field === 'conductsCoffeeResearch') {
        if (user[field] === null || user[field] === undefined) missingFields.push(field);
      } else if (!user[field] || (typeof user[field] === 'string' && user[field].trim() === '')) {
        missingFields.push(field);
      }
    });
    return missingFields;
  };

  const verifyOccupationEmptyness = () => {
    if (!user.company && !user.educationalInstitution && !user.areaplanted && !user.plantingChallengesDescription && !user.cropType &&
      user.isInvolvedWithBiological === null && user.isFarmerChild === null &&
      user.conductsCoffeeResearch === null && !user.otherDescription) {
      return true;
    }
    return false;
  };
  //#endregion

  //#region Manipulação de Dados
  const handleOccupationChange = (newOccupation) => {
    if (user.subtype && user.subtype !== newOccupation && !verifyOccupationEmptyness()) {
      setPendingOccupation(newOccupation);
      setShowOccupationModal(true);
    } else {
      setUser(prev => ({ ...prev, subtype: newOccupation }));
    }
  };

  const confirmOccupationChange = () => {
    setUser(prev => ({
      ...prev,
      subtype: pendingOccupation,
      company: '', educationalInstitution: '', areaplanted: '',
      plantingChallengesDescription: '', cropType: '',
      isInvolvedWithBiological: null, isFarmerChild: null,
      conductsCoffeeResearch: null, otherDescription: ''
    }));
    setShowOccupationModal(false);
    setPendingOccupation('');
  };

  // Validação por passo (antes de avançar)
  const validateStep1 = () => {
    if (!user.name.trim()) { showError(`${t.missingFields} ${t.fullName}`); return false; }
    if (!user.email || !validateEmail(user.email)) { setEmailError(t.emailInvalid); showError(`${t.missingFields} ${t.email}`); return false; }
    if (!user.password || user.password.length < 6) { showError(`${t.missingFields} ${t.password}`); return false; }
    return true;
  };

  const validateStep2 = () => {
    const missing = ['state', 'city', 'subtype', 'dateBirth'].filter(f =>
      !user[f] || (typeof user[f] === 'string' && user[f].trim() === ''));
    if (missing.length) {
      const labels = { state: t.state, city: t.city, subtype: t.occupation, dateBirth: t.dateOfBirth };
      showError(`${t.missingFields} ${missing.map(f => labels[f]).join(', ')}`);
      return false;
    }
    const occ = validateOccupationFields();
    if (occ.length) {
      showError(`${t.missingFields} ${occ.map(f => t.fieldNames[f] || f).join(', ')}`);
      return false;
    }
    return true;
  };

  const goNext = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    setStep(s => Math.min(3, s + 1));
  };
  const goBack = () => setStep(s => Math.max(1, s - 1));

  // Passo final: revalida tudo e abre o modal de termos
  const handleRegisterClick = (e) => {
    e.preventDefault();
    if (!validateStep1() || !validateStep2()) return;
    setShowTermsModal(true);
  };

  const handleTermsAccept = async () => {
    if (!termsAccepted || !dataCollectionConsent) {
      showError(t.termsValidationError);
      return;
    }
    setSubmitting(true);
    try {
      const userData = {
        username: user.username || usernameFromEmail(user.email),
        password: user.password,
        name: user.name,
        role: 'READER',
        email: user.email,
        subtype: user.subtype,
        company: user.company,
        plantingChallengesDescription: user.plantingChallengesDescription,
        isInvolvedWithBiological: user.isInvolvedWithBiological,
        isFarmerChild: user.isFarmerChild,
        conductsCoffeeResearch: user.conductsCoffeeResearch,
        cropType: user.cropType,
        state: user.state,
        city: user.city,
        areaplanted: user.areaplanted,
        educationalInstitution: user.educationalInstitution,
        permissionToCollectGeographicData: true,
        platformInterest: user.platformInterest,
        cpf: user.cpf,
        dateBirth: user.dateBirth,
        otherDescription: user.otherDescription,
      };
      await axios.post(API_ENDPOINTS.USERS_REGISTER, userData);
      showSuccess(t.successMessage, 7000, { details: t.detailMessage });
      setShowTermsModal(false);
      setTermsAccepted(false);
      setDataCollectionConsent(false);
      setTimeout(() => navigate('/login'), 4000);
    } catch (error) {
      // Mostra a mensagem que o backend devolve (409 = e-mail já cadastrado, 400 = dado
      // inválido). Antes o erro real era descartado e todo problema virava a mesma
      // mensagem genérica, o que tornava a falha impossível de diagnosticar.
      const apiMessage = error.response?.data?.message;
      showError(apiMessage || t.errorMessage);
      setShowTermsModal(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTermsCancel = () => {
    setShowTermsModal(false);
    setTermsAccepted(false);
    setDataCollectionConsent(false);
  };

  // Controle segmentado Não/Sim
  const renderBool = (field, label, onYesExtra) => (
    <div className="profile-field">
      <label className="form-label">{label}</label>
      <div className="seg-toggle" role="group" aria-label={label}>
        <button type="button" className={`seg-btn${user[field] === false ? ' active' : ''}`}
          onClick={() => setUser({ ...user, [field]: false, ...(field === 'isFarmerChild' ? { cropType: '', areaplanted: '' } : {}) })}>
          {t.no}
        </button>
        <button type="button" className={`seg-btn${user[field] === true ? ' active' : ''}`}
          onClick={() => setUser({ ...user, [field]: true, ...(onYesExtra || {}) })}>
          {t.yes}
        </button>
      </div>
    </div>
  );
  //#endregion

  //#region Dados derivados
  const occupations = [
    { value: 'PRODUCER', label: t.producer, icon: <FaTractor /> },
    { value: 'RESEARCHER', label: t.researcher, icon: <FaFlask /> },
    { value: 'STUDENT', label: t.student, icon: <FaUserGraduate /> },
    { value: 'CONSULTANT', label: t.consultant, icon: <FaUserTie /> },
    { value: 'OTHER', label: t.other, icon: <FaEllipsisH /> },
  ];
  const steps = [t.stepLabel1, t.stepLabel2, t.stepLabel3];
  //#endregion

  //#region Renderização
  return (
    <div className="profile-editor">
      <h2 className="text-center mb-1 mt-2 profile-name">{t.createAccount}</h2>
      <p className="text-center small text-muted mb-2">
        {t.alreadyAccount}{' '}
        <button type="button" className="btn btn-link btn-sm p-0 align-baseline text-decoration-none"
          onClick={() => setLoginModalOpen(true)}>{t.enterLink}</button>
      </p>

      {/* Stepper */}
      <div className="reg-stepper">
        {steps.map((label, i) => {
          const n = i + 1;
          const state = n < step ? 'done' : n === step ? 'current' : '';
          return (
            <Fragment key={label}>
              <div className={`reg-step ${state}`}>
                <span className="reg-step-dot">{n < step ? <FaCheck size={11} /> : n}</span>
                <span className="reg-step-label">{label}</span>
              </div>
              {n < steps.length && <span className={`reg-step-line${n < step ? ' done' : ''}`} />}
            </Fragment>
          );
        })}
      </div>
      <div className="text-center small text-muted mb-3">{t.stepCounter} {step} {t.of} 3</div>

      <form onSubmit={handleRegisterClick}>
        {/* STEP 1 — ACESSO */}
        {step === 1 && (
          <section className="profile-section border-0 mb-0">
            <h3 className="profile-section-title">{t.step1Title}</h3>
            <p className="text-muted small mt-n2 mb-3">{t.step1Subtitle}</p>

            <div className="profile-field">
              <label className="form-label">{t.yourName}</label>
              <input type="text" className="form-control form-control-lg"
                value={user.name} onChange={(e) => setUser({ ...user, name: e.target.value })}
                placeholder={t.yourName} required />
            </div>

            <div className="profile-field">
              <label className="form-label">{t.yourEmail}</label>
              <input type="email" name="email" autoComplete="email"
                className={`form-control ${emailError && 'is-invalid'}`}
                value={user.email}
                onChange={(e) => { setUser({ ...user, email: e.target.value, username: usernameFromEmail(e.target.value) }); setEmailError(''); }}
                onBlur={(e) => { if (e.target.value && !validateEmail(e.target.value)) setEmailError(t.emailInvalid); }}
                placeholder={t.placeholderEmail} required />
              {emailError && <div className="invalid-feedback">{emailError}</div>}
            </div>

            <div className="profile-field mb-2">
              <label className="form-label">{t.createPassword}</label>
              <div className="input-group">
                <input type={showPwd ? 'text' : 'password'} name="password" autoComplete="new-password"
                  className="form-control"
                  value={user.password}
                  onChange={(e) => { setUser({ ...user, password: e.target.value }); setPasswordStrength(getPasswordStrength(e.target.value)); }}
                  placeholder={t.placeholderPassword} required />
                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowPwd(!showPwd)}
                  title={showPwd ? t.hidePwd : t.showPwd} tabIndex={-1}>
                  {showPwd ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {user.password && (
                <div className="mt-2">
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <small className="text-muted">{t.passwordStrengthLabel || 'Força da senha'}:</small>
                    <small style={{ color: passwordStrength.color, fontWeight: 'bold' }}>{passwordStrength.label}</small>
                  </div>
                  <div className="progress" style={{ height: '5px' }}>
                    <div className="progress-bar" role="progressbar"
                      style={{ width: `${(passwordStrength.level / 3) * 100}%`, backgroundColor: passwordStrength.color }} />
                  </div>
                  {passwordStrength.level < 3 && passwordStrength.checks && (
                    <ul className="small text-muted mb-0 mt-2" style={{ paddingLeft: '1.2rem' }}>
                      {!passwordStrength.checks.minLength && <li>{t.passwordErrorMinLength}</li>}
                      {!passwordStrength.checks.hasUpperCase && <li>{t.passwordErrorUpperCase}</li>}
                      {!passwordStrength.checks.hasNumber && <li>{t.passwordErrorNumber}</li>}
                      {!passwordStrength.checks.hasSpecialChar && <li>{t.passwordErrorSpecialChar}</li>}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <div className="reg-security"><FaLock className="me-2" />{t.securityNote}</div>
          </section>
        )}

        {/* STEP 2 — VOCÊ */}
        {step === 2 && (
          <section className="profile-section border-0 mb-0">
            <h3 className="profile-section-title">{t.step2Title}</h3>
            <p className="text-muted small mt-n2 mb-3">{t.step2Subtitle}</p>

            <div className="row g-3">
              <div className="col-sm-6 profile-field mb-0">
                <label className="form-label">{t.state}</label>
                <BrandSelect className="w-100" searchable searchPlaceholder={t.selectState} noResultsText={t.selectState}
                  value={user.state} onChange={(v) => setUser({ ...user, state: v })} placeholder={t.selectState}
                  options={[{ value: "", label: t.selectState }, ...estado.map((s) => ({ value: s.sigla, label: s.nome }))]} />
              </div>
              <div className="col-sm-6 profile-field mb-0">
                <label className="form-label">{t.city}</label>
                <BrandSelect className="w-100" searchable searchPlaceholder={t.selectCity} noResultsText={t.selectCity}
                  value={user.city} onChange={(v) => setUser({ ...user, city: v })} placeholder={t.selectCity}
                  options={[{ value: "", label: t.selectCity }, ...cityList.map((c) => ({ value: c.nome, label: c.nome }))]} />
              </div>
            </div>

            <div className="profile-field mt-3">
              <label className="form-label">{t.howWork}</label>
              <div className="occ-grid">
                {occupations.map((o) => (
                  <button type="button" key={o.value}
                    className={`occ-card${user.subtype === o.value ? ' active' : ''}`}
                    onClick={() => handleOccupationChange(o.value)}>
                    <span className="occ-icon">{o.icon}</span>
                    <span className="occ-label">{o.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Campos condicionais por ocupação */}
            {user.subtype === 'PRODUCER' && (
              <div className="profile-subblock">
                <div className="profile-field">
                  <label className="form-label">{t.cropType}</label>
                  <BrandSelect className="w-100" searchable searchPlaceholder={t.selectCropType} noResultsText={t.selectCropType}
                    value={user.cropType} onChange={(v) => setUser({ ...user, cropType: v })} placeholder={t.selectCropType}
                    options={[{ value: "", label: t.selectCropType }, ...cropData.map((c) => ({ value: c, label: c }))]} />
                </div>
                {user.cropType && (
                  <div className="profile-field">
                    <label className="form-label">{t.plantedArea} {user.cropType} (ha)*</label>
                    <input type="number" className="form-control" value={user.areaplanted}
                      onChange={(e) => setUser({ ...user, areaplanted: e.target.value })} placeholder={t.plantedAreaPlaceholder} required />
                  </div>
                )}
                <div className="profile-field mb-0">
                  <label className="form-label">{t.plantingChallenges}</label>
                  <textarea className="form-control" rows="3" value={user.plantingChallengesDescription}
                    onChange={(e) => setUser({ ...user, plantingChallengesDescription: e.target.value })} placeholder={t.plantingChallengesPlaceholder} required />
                </div>
              </div>
            )}

            {user.subtype === 'STUDENT' && (
              <div className="profile-subblock">
                <div className="profile-field">
                  <label className="form-label">{t.educationalInstitution}</label>
                  <input type="text" className="form-control" value={user.educationalInstitution}
                    onChange={(e) => setUser({ ...user, educationalInstitution: e.target.value })} placeholder={t.placeholderEducationalInstitution} required />
                </div>
                {renderBool('isFarmerChild', t.isFarmerChild)}
                {user.isFarmerChild && (
                  <>
                    <div className="profile-field">
                      <label className="form-label">{t.cropType}</label>
                      <BrandSelect className="w-100" searchable searchPlaceholder={t.selectCropType} noResultsText={t.selectCropType}
                        value={user.cropType} onChange={(v) => setUser({ ...user, cropType: v })} placeholder={t.selectCropType}
                        options={[{ value: "", label: t.selectCropType }, ...cropData.map((c) => ({ value: c, label: c }))]} />
                    </div>
                    {user.cropType && (
                      <div className="profile-field mb-0">
                        <label className="form-label">{t.plantedArea} {user.cropType} (ha)*</label>
                        <input type="number" className="form-control" value={user.areaplanted}
                          onChange={(e) => setUser({ ...user, areaplanted: e.target.value })} placeholder={t.plantedAreaPlaceholder} required />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {user.subtype === 'CONSULTANT' && (
              <div className="profile-subblock">
                <div className="profile-field">
                  <label className="form-label">{t.company}</label>
                  <input type="text" className="form-control" value={user.company}
                    onChange={(e) => setUser({ ...user, company: e.target.value })} placeholder={t.placeholderCompany} required />
                </div>
                {renderBool('isInvolvedWithBiological', t.isInvolvedWithBiological)}
              </div>
            )}

            {user.subtype === 'RESEARCHER' && (
              <div className="profile-subblock">
                <div className="profile-field">
                  <label className="form-label">{t.educationalInstitution}</label>
                  <input type="text" className="form-control" value={user.educationalInstitution}
                    onChange={(e) => setUser({ ...user, educationalInstitution: e.target.value })} placeholder={t.placeholderEducationalInstitution} required />
                </div>
                {renderBool('conductsCoffeeResearch', t.conductsCoffeeResearch)}
                {renderBool('isInvolvedWithBiological', t.isInvolvedWithBiological)}
              </div>
            )}

            {user.subtype === 'OTHER' && (
              <div className="profile-subblock">
                <div className="profile-field mb-0">
                  <label className="form-label">{t.otherDescription}</label>
                  <input type="text" className="form-control" value={user.otherDescription}
                    onChange={(e) => setUser({ ...user, otherDescription: e.target.value })} placeholder={t.placeholderOtherDescription} required />
                </div>
              </div>
            )}

            <div className="row g-3 mt-1">
              <div className="col-sm-6 profile-field mb-0">
                <label className="form-label">{t.cpf}</label>
                <input type="text" className={`form-control ${cpfError && 'is-invalid'}`} value={user.cpf}
                  onChange={(e) => { setCpfError(''); setUser({ ...user, cpf: formatCPF(e.target.value) }); }} placeholder={t.placeholderCpf} />
                <div className="field-help">{t.cpfHelp}</div>
              </div>
              <div className="col-sm-6 profile-field mb-0">
                <label className="form-label">{t.dateOfBirth}</label>
                <input type="date" className="form-control" value={user.dateBirth}
                  onChange={(e) => setUser({ ...user, dateBirth: e.target.value })} required />
              </div>
            </div>
          </section>
        )}

        {/* STEP 3 — CONFIRMAR */}
        {step === 3 && (
          <section className="profile-section border-0 mb-0">
            <h3 className="profile-section-title">{t.step3Title}</h3>
            <p className="text-muted small mt-n2 mb-3">{t.step3Subtitle}</p>

            <div className="reg-summary">
              <div><strong>{user.name}</strong></div>
              <div className="text-muted small">{user.email}</div>
              <div className="text-muted small">
                {user.city ? `${user.city} · ` : ''}{user.state}
                {user.subtype ? ` · ${(occupations.find(o => o.value === user.subtype) || {}).label}` : ''}
              </div>
            </div>

            <div className="profile-field mt-3 mb-0">
              <label className="form-label">{t.interestQuestion}</label>
              <textarea className="form-control" rows="3" value={user.platformInterest}
                onChange={(e) => setUser({ ...user, platformInterest: e.target.value })} placeholder={t.platformInterestPlaceholder} />
            </div>
          </section>
        )}

        {/* Footer fixo */}
        <div className="profile-footer">
          {step > 1 ? (
            <button type="button" className="btn btn-outline-secondary" onClick={goBack}>
              <FaArrowLeft className="me-2" />{t.backBtn}
            </button>
          ) : <span />}
          {step < 3 ? (
            <button type="button" className="btn btn-brand" onClick={goNext}>
              {t.continueBtn} →
            </button>
          ) : (
            <button type="submit" className="btn btn-brand">
              <FaCheck className="me-2" />{t.finishCreate}
            </button>
          )}
        </div>
      </form>

      {/* MODAL DE TERMOS */}
      {showTermsModal && (
        <div className="modal fade show reg-modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.55)' }} tabIndex="-1">
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t.termsModalTitle}</h5>
              </div>
              <div className="modal-body">
                <div dangerouslySetInnerHTML={{ __html: t.termsContent }} />
                <hr />
                <div className="form-check form-check-agree mb-3">
                  <input className="form-check-input" type="checkbox" id="dataCollectionConsent"
                    checked={dataCollectionConsent} onChange={(e) => setDataCollectionConsent(e.target.checked)} />
                  <label className="form-check-label" htmlFor="dataCollectionConsent">{t.dataCollectionLabel}</label>
                </div>
                <div className="form-check form-check-agree mb-3">
                  <input className="form-check-input" type="checkbox" id="termsAccepted"
                    checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} />
                  <label className="form-check-label" htmlFor="termsAccepted">{t.termsAcceptanceLabel}</label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={handleTermsCancel}>{t.cancelButton}</button>
                <button type="button" className="btn btn-brand" onClick={handleTermsAccept}
                  disabled={!termsAccepted || !dataCollectionConsent || submitting}>
                  {submitting ? (<><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>{t.creating}</>) : t.acceptButton}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE TROCA DE OCUPAÇÃO */}
      {showOccupationModal && (
        <div className="modal fade show reg-modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.55)' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t.occupation}</h5>
              </div>
              <div className="modal-body">
                <p>{t.occupationChangeWarning}</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary"
                  onClick={() => { setShowOccupationModal(false); setPendingOccupation(''); }}>{t.cancel}</button>
                <button type="button" className="btn btn-brand" onClick={confirmOccupationChange}>{t.confirmChange}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
  //#endregion
}

export default Register;
